import React, { useState } from "react";
import {
  Box, Typography, TextField, Button, Table,
  TableHead, TableBody, TableRow, TableCell, TableContainer
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import productionQuantityReportService from "../../../services/report/production_quantity_report.Service";

const printStyles = `
  .pqr-print-header { display: none; }
  @media print {
    .pqr-print-header { display: block !important; margin-bottom: 16px; }
    body * { visibility: hidden; }
    .pqr-printable-area, .pqr-printable-area * { visibility: visible; }
    .pqr-printable-area { position: absolute; left: 0; top: 0; width: 100%; }
    .pqr-printable-area table { border-collapse: collapse; width: 100%; }
    .pqr-printable-area th, .pqr-printable-area td { border: 1px solid #ccc; padding: 8px 12px; font-size: 13px; }
    .pqr-printable-area th { background: #1C3A5E !important; color: #fff !important; -webkit-print-color-adjust: exact; }
    .no-print { display: none !important; }
  }
`;

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px", fontSize: "13px", height: "38px", background: "#fafafa",
    "& fieldset": { borderColor: "#E2E8F0" },
    "&:hover fieldset": { borderColor: "#CBD5E0" },
    "&.Mui-focused fieldset": { borderColor: "#2B6CB0" },
  },
};

const thSx = {
  background: "#1C3A5E", color: "#fff", fontSize: "12px",
  fontWeight: 600, padding: "10px 12px", textAlign: "center"
};

const tdSx = { fontSize: "13px", padding: "10px 12px", color: "#2D3748", textAlign: "center" };

const ProductionQuantityReport = () => {
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  React.useEffect(() => {
    const styleSheet = document.createElement('style');
    styleSheet.type = 'text/css';
    styleSheet.innerText = printStyles;
    document.head.appendChild(styleSheet);
    return () => { document.head.removeChild(styleSheet); };
  }, []);

  const fetchReport = async () => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await productionQuantityReportService.getSummary({
        start_date: fromDate.format("YYYY-MM-DD"),
        end_date: toDate.format("YYYY-MM-DD"),
      });
      setReportData(res);
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.response?.data?.message || "Failed to fetch report");
      setReportData(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => { window.print(); };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ background: "#F4F7FB", minHeight: "100vh", padding: "20px 24px 40px" }}>

        <Typography className="no-print" sx={{ fontSize: "16px", fontWeight: 700, color: "#1C3A5E", mb: 2.5 }}>
          Production Quantity Report
        </Typography>

        {/* ── Filter Card ── */}
        <Box className="no-print" sx={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "10px", p: 2.5, mb: 2.5 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end", flexWrap: "wrap" }}>
            <Box>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#2063b6", mb: "4px" }}>From Date</Typography>
              <DatePicker
                format="DD/MM/YYYY" value={fromDate}
                onChange={v => setFromDate(v)}
                slotProps={{ textField: { size: "small", sx: inputSx } }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#2063b6", mb: "4px" }}>To Date</Typography>
              <DatePicker
                format="DD/MM/YYYY" value={toDate}
                onChange={v => setToDate(v)}
                slotProps={{ textField: { size: "small", sx: inputSx } }}
              />
            </Box>
            <Button
              onClick={fetchReport}
              disabled={!fromDate || !toDate || loading}
              sx={{ height: "38px", px: 3, background: "#1C3A5E", color: "#fff !important", borderRadius: "8px", fontSize: "13px", fontWeight: 600, textTransform: "none", "&:hover": { background: "#2B6CB0" } }}
            >
              {loading ? "Loading..." : "Show"}
            </Button>
            {reportData && (
              <Button
                onClick={handlePrint}
                sx={{ height: "38px", px: 3, background: "#2B6CB0", color: "#fff !important", borderRadius: "8px", fontSize: "13px", fontWeight: 600, textTransform: "none", "&:hover": { background: "#1C3A5E" } }}
              >
                🖨️ Print
              </Button>
            )}
          </Box>
          {errorMsg && (
            <Typography sx={{ fontSize: "12px", color: "#E53E3E", mt: 1.5 }}>{errorMsg}</Typography>
          )}
        </Box>

        {/* ── Report Table ── */}
        {reportData && (
          <Box className="pqr-printable-area" sx={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: "10px", overflow: "hidden" }}>

            <Box className="pqr-print-header">
              <Typography sx={{ fontSize: "18px", fontWeight: 700, textAlign: "center", color: "#1C3A5E", mb: 1 }}>
                PRODUCTION QUANTITY REPORT
              </Typography>
              <Typography sx={{ fontSize: "13px", textAlign: "center" }}>
                <strong>From:</strong> {fromDate.format("DD/MM/YYYY")} &nbsp;&nbsp;
                <strong>To:</strong> {toDate.format("DD/MM/YYYY")}
              </Typography>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={thSx}>Quantity Filled in Cylinder (CUM)</TableCell>
                    <TableCell sx={thSx}>Quantity Topped up (CUM)</TableCell>
                    <TableCell sx={thSx}>Quantity Billed (CUM)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={tdSx}>{reportData.qty_filled_cum}</TableCell>
                    <TableCell sx={tdSx}>{reportData.qty_topped_up_cum}</TableCell>
                    <TableCell sx={tdSx}>{reportData.qty_billed_cum}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ p: 2, borderTop: "1px solid #E2E8F0", display: "flex", justifyContent: "flex-end" }}>
              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1C3A5E" }}>
                Total Production Quantity: <span style={{ color: "#2F855A" }}>{reportData.total_production_qty_cum} CUM</span>
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default ProductionQuantityReport;
